import type {
  AnnotationHandle,
  BlockHandle,
  CaseworkHandle,
  ColumnHandle,
  ContentTemplateInstanceHandle,
  DocEntityHandle,
  DormerHandle,
  ElectricalRuleHandle,
  FacadeHandle,
  FloorHandle,
  FloorSystemHandle,
  FoundationSystemHandle,
  LevelHandle,
  MeasurementHandle,
  OpeningHandle,
  PaletteHandle,
  RailingHandle,
  RoofEdgeHandle,
  RoofHandle,
  RoofOpeningHandle,
  RoofSystemHandle,
  RoomHandle,
  RoomLayoutInstanceHandle,
  StairBlockHandle,
  StairSystemHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { PolyLine2 } from "geom/polyLine2Types";

export enum BuildingGenerationIssueType {
  ROOM_OVERLAP = "ROOM_OVERLAP",
  WINDOW_OPENING_EXCEEDS_BOUNDARY = "WINDOW_OPENING_EXCEEDS_BOUNDARY",
  DOOR_OPENING_EXCEEDS_BOUNDARY = "DOOR_OPENING_EXCEEDS_BOUNDARY",
  STAIRWAY_CONNECTION_ISSUE = "STAIRWAY_CONNECTION_ISSUE",
  STAIRWAY_ENTRANCE_WALL_MISSING = "STAIRWAY_ENTRANCE_WALL_MISSING",
  STAIRWAY_MISSING_LEVEL = "STAIRWAY_MISSING_LEVEL",
  STAIR_RUN_TYPE_INCOMPATIBLE = "STAIR_RUN_TYPE_INCOMPATIBLE",
  STAIR_PLACEMENT_ROOM_MISSING = "STAIR_PLACEMENT_ROOM_MISSING",
  STAIR_GENERATION_ISSUE = "STAIR_GENERATION_ISSUE",
  RAILING_LEVEL_UNDEFINED = "RAILING_LEVEL_UNDEFINED",
  UNKNOWN_WINDOW_TYPE = "UNKNOWN_WINDOW_TYPE",
  UNKNOWN_DOOR_TYPE = "UNKNOWN_DOOR_TYPE",
  UNKNOWN_CASEWORK_TYPE = "UNKNOWN_CASEWORK_TYPE",
  MISSING_PROFILE_REFERENCE = "MISSING_PROFILE_REFERENCE",
  INVALID_DOOR_HEIGHT = "INVALID_DOOR_HEIGHT",
  INVALID_WINDOW_SASH_GEOMETRY = "INVALID_WINDOW_SASH_GEOMETRY",
  INVALID_WINDOW_SHUTTER_SIZE = "INVALID_WINDOW_SHUTTER_SIZE",
  INVALID_WINDOW_SHUTTER_HEIGHT = "INVALID_WINDOW_SHUTTER_HEIGHT",
  INVALID_WINDOW_SHUTTER_GEOMETRY = "INVALID_WINDOW_SHUTTER_GEOMETRY",
  SKYLIGHT_OPENING_EXCEEDS_BOUNDARY = "SKYLIGHT_OPENING_EXCEEDS_BOUNDARY",
  INVALID_FOUNDATION_CONDITION = "INVALID_FOUNDATION_CONDITION",
  INVALID_ANNOTATION_EDGE_ID = "INVALID_ANNOTATION_EDGE_ID",
  INVALID_FREE_FORM_ROOF_PERIMETER = "INVALID_FREE_FORM_ROOF_PERIMETER",
  COLUMN_GENERATION_ISSUE = "COLUMN_GENERATION_ISSUE",
  MEASUREMENT_GENERATION_ISSUE = "MEASUREMENT_GENERATION_ISSUE",
  ROOF_EDGE_SLOPE_ISSUE = "ROOF_EDGE_SLOPE_ISSUE",
  INVALID_DATUM = "INVALID_DATUM",
  LEVEL_CIRCULAR_DEPENDENCY = "LEVEL_CIRCULAR_DEPENDENCY",
  ELECTRICAL_RULE_DUPLICATE_ID = "ELECTRICAL_RULE_DUPLICATE_ID",
  FETCH_ISSUE = "FETCH_ISSUE",
  ANNOTATION_MISSING_PARENT = "ANNOTATION_MISSING_PARENT",
  HOLE_ON_LEVEL = "HOLE_ON_LEVEL",
  CEILING_GENERATION_ISSUE = "CEILING_GENERATION_ISSUE",
  MAX_BSP_DEPTH_EXCEEDED = "MAX_BSP_DEPTH_EXCEEDED",
  FLOOR_GENERATION_ERROR = "FLOOR_GENERATION_ERROR",
  WALL_FRAMING_GENERATION_ERROR = "WALL_FRAMING_GENERATION_ERROR",
  ROOF_SYSTEM_GENERATION_ERROR = "ROOF_SYSTEM_GENERATION_ERROR",
  BLOCK_MISSING_LEVEL = "BLOCK_MISSING_LEVEL",
}

export type ParamDetails = StringParamDetails | RangeParamDetails | SetParamDetails;

interface StringParamDetails {
  readonly type: "STRING";
  name: string;
  description?: string;
  value?: string;
}

type RangeIssueType = "MAX_EXCEEDED" | "MIN_EXCEEDED";

/**
 * Use this to describe a parameter that falls outside
 * of an allowed range (min/max are inclusive)
 */
interface RangeParamDetails {
  readonly type: "RANGE";

  issueType: RangeIssueType;
  description?: string;

  /** inclusive min */
  min?: number;

  /** inclusive max */
  max?: number;

  value: number;
}

/**
 * Use this to describe a parameter that falls outside
 * of an allowed set.
 */
interface SetParamDetails {
  readonly type: "SET";
  allowedSet: string[];
  value: string;
}

export type BuildingGenerationIssue =
  | RoomOverlapIssue
  | WindowOpeningExceedsBoundaryIssue
  | DoorOpeningExceedsBoundaryIssue
  | UnknownWindowTypeIssue
  | UnknownDoorTypeIssue
  | UnknownCaseworkTypeIssue
  | MissingProfileReferenceIssue
  | StairwayConnectionIssue
  | StairwayEntranceWallMissingIssue
  | StairwayMissingLevel
  | StairRunTypeIncompatible
  | StairPlacementRoomMissing
  | StairGenerationIssue
  | RailingLevelUndefined
  | DoorIssue
  | WindowUnitIssue
  | WindowShutterIssue
  | SkylightOpeningExceedsBoundaryIssue
  | InvalidFoundationConditionIssue
  | InvalidAnnotationEdgeId
  | InvalidFreeFormRoofPerimeterIssue
  | ColumnGenerationIssue
  | MeasurementGenerationIssue
  | RoofEdgeSlopeIssue
  | InvalidDatumIssue
  | LevelCircularDependencyIssue
  | ElectricalRuleDuplicateId
  | AnnotationMissingParent
  | FetchIssue
  | HoleOnLevelIssue
  | CeilingGenerationIssue
  | MaxBSPDepthExceededIssue
  | FloorGenerationError
  | WallFramingGenerationError
  | RoofSystemGenerationError
  | BlockMissingLevel;

export interface FloorGenerationError {
  readonly type: BuildingGenerationIssueType.FLOOR_GENERATION_ERROR;
  readonly message: string;
  readonly handle: FloorHandle | FloorSystemHandle;
}

export interface WallFramingGenerationError {
  readonly type: BuildingGenerationIssueType.WALL_FRAMING_GENERATION_ERROR;
  readonly message: string;
  readonly handle?: undefined;
}

/** The whole roof system was skipped because generation threw partway through. */
export interface RoofSystemGenerationError {
  readonly type: BuildingGenerationIssueType.ROOF_SYSTEM_GENERATION_ERROR;
  readonly message: string;
  readonly handle: RoofSystemHandle;
}

interface UnknownWindowTypeIssue {
  readonly type: BuildingGenerationIssueType.UNKNOWN_WINDOW_TYPE;
  readonly windowType: string;
  readonly handle: OpeningHandle | RoomLayoutInstanceHandle | RoofOpeningHandle | RoomHandle;
}

export interface MissingProfileReferenceIssue {
  readonly type: BuildingGenerationIssueType.MISSING_PROFILE_REFERENCE;
  readonly profileId: string;
  readonly handle: DocEntityHandle;
  readonly field?: string;
}

interface WindowUnitIssue {
  readonly type: BuildingGenerationIssueType.INVALID_WINDOW_SASH_GEOMETRY;
  readonly handle?: OpeningHandle | RoofOpeningHandle | DormerHandle | RoomLayoutInstanceHandle;
}

interface UnknownDoorTypeIssue {
  readonly type: BuildingGenerationIssueType.UNKNOWN_DOOR_TYPE;
  readonly doorType: string;
  readonly handle: OpeningHandle | RoomLayoutInstanceHandle;
}

interface UnknownCaseworkTypeIssue {
  readonly type: BuildingGenerationIssueType.UNKNOWN_CASEWORK_TYPE;
  readonly caseworkType: string;
  readonly handle: CaseworkHandle | RoomLayoutInstanceHandle;
}

export interface DoorOpeningExceedsBoundaryIssue {
  readonly type: BuildingGenerationIssueType.DOOR_OPENING_EXCEEDS_BOUNDARY;
  readonly handle?: OpeningHandle | RoomLayoutInstanceHandle;
  readonly params?: ParamDetails[];
  readonly paramAdjustment?: number;
}

interface StairwayConnectionIssue {
  readonly type: BuildingGenerationIssueType.STAIRWAY_CONNECTION_ISSUE;
  readonly handle?: StairSystemHandle;
  readonly missingRoomOnLevel: number;
  readonly missingRoomName: string;
}

interface StairwayEntranceWallMissingIssue {
  readonly type: BuildingGenerationIssueType.STAIRWAY_ENTRANCE_WALL_MISSING;
  readonly handle?: StairSystemHandle;
}

interface StairwayMissingLevel {
  readonly type: BuildingGenerationIssueType.STAIRWAY_MISSING_LEVEL;
  readonly handle?: StairSystemHandle;
  readonly missingLevelId: string;
}

interface BlockMissingLevel {
  readonly type: BuildingGenerationIssueType.BLOCK_MISSING_LEVEL;
  readonly handle?: BlockHandle;
  readonly missingLevelId: string;
  readonly docRefId?: string;
  readonly docRefName?: string;
}

interface StairRunTypeIncompatible {
  readonly type: BuildingGenerationIssueType.STAIR_RUN_TYPE_INCOMPATIBLE;
  readonly handle?: StairBlockHandle;
}

interface StairPlacementRoomMissing {
  readonly type: BuildingGenerationIssueType.STAIR_PLACEMENT_ROOM_MISSING;
  readonly handle?: StairSystemHandle;
}

interface StairGenerationIssue {
  readonly type: BuildingGenerationIssueType.STAIR_GENERATION_ISSUE;
  readonly message: string;
  readonly handle?: StairSystemHandle | StairBlockHandle;
}

interface RailingLevelUndefined {
  readonly type: BuildingGenerationIssueType.RAILING_LEVEL_UNDEFINED;
  readonly handle?: RailingHandle;
}

export interface ElectricalRuleDuplicateId {
  readonly type: BuildingGenerationIssueType.ELECTRICAL_RULE_DUPLICATE_ID;
  readonly unusedHandles: ElectricalRuleHandle[];
  readonly handle: ElectricalRuleHandle;
}

export interface FetchIssue {
  readonly type: BuildingGenerationIssueType.FETCH_ISSUE;
  readonly message: string;
  readonly handle?: PaletteHandle;
}

export interface WindowOpeningExceedsBoundaryIssue {
  readonly type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY;
  readonly handle?: OpeningHandle | RoofOpeningHandle | DormerHandle | RoomLayoutInstanceHandle;
  readonly params?: ParamDetails[];
  readonly paramAdjustment?: number;
}

interface DoorIssue {
  readonly type: BuildingGenerationIssueType.INVALID_DOOR_HEIGHT;
  readonly doorType: string;
  readonly handle?: OpeningHandle | RoomLayoutInstanceHandle;
  readonly message?: string;
}
interface WindowShutterIssue {
  readonly type:
    | BuildingGenerationIssueType.INVALID_WINDOW_SHUTTER_SIZE
    | BuildingGenerationIssueType.INVALID_WINDOW_SHUTTER_HEIGHT
    | BuildingGenerationIssueType.INVALID_WINDOW_SHUTTER_GEOMETRY;
  readonly handle?: OpeningHandle | RoofOpeningHandle | DormerHandle | RoomLayoutInstanceHandle;
}

interface SkylightOpeningExceedsBoundaryIssue {
  readonly type: BuildingGenerationIssueType.SKYLIGHT_OPENING_EXCEEDS_BOUNDARY;
  readonly handle?: RoofHandle;
  readonly params?: ParamDetails[];
}

interface InvalidFoundationConditionIssue {
  readonly type: BuildingGenerationIssueType.INVALID_FOUNDATION_CONDITION;
  readonly invalidCondition: string;
  readonly fallbackCondition: string;
  readonly handle?: FoundationSystemHandle;
}

interface InvalidAnnotationEdgeId {
  readonly type: BuildingGenerationIssueType.INVALID_ANNOTATION_EDGE_ID;
  readonly edgeId?: string;
  readonly handle?: AnnotationHandle | ContentTemplateInstanceHandle;
}

interface InvalidFreeFormRoofPerimeterIssue {
  readonly type: BuildingGenerationIssueType.INVALID_FREE_FORM_ROOF_PERIMETER;
  readonly handle?: RoofHandle;
}

/**
 * The RoomOverlapIssue is handled as a critical issue. It is presented to the user
 * as an error, but it doesn't prevent building generation from proceeding.
 */
export interface RoomOverlapIssue {
  readonly type: BuildingGenerationIssueType.ROOM_OVERLAP;
  readonly handle: RoomHandle[];
  readonly level: LevelHandle;
}

export interface ColumnGenerationIssue {
  readonly type: BuildingGenerationIssueType.COLUMN_GENERATION_ISSUE;
  readonly handle?: ColumnHandle;
  readonly message: string;
  readonly levelId: string;
}

export interface MeasurementGenerationIssue {
  readonly type: BuildingGenerationIssueType.MEASUREMENT_GENERATION_ISSUE;
  readonly handle?: MeasurementHandle;
  readonly message: string;
}

export interface RoofEdgeSlopeIssue {
  readonly type: BuildingGenerationIssueType.ROOF_EDGE_SLOPE_ISSUE;
  readonly handle?: RoofEdgeHandle;
}

export interface InvalidDatumIssue {
  readonly type: BuildingGenerationIssueType.INVALID_DATUM;
  readonly handle: FacadeHandle | LevelHandle;
  readonly message: string;
}

export interface LevelCircularDependencyIssue {
  readonly type: BuildingGenerationIssueType.LEVEL_CIRCULAR_DEPENDENCY;
  readonly handle: LevelHandle;
  readonly message: string;
}

export interface AnnotationMissingParent {
  readonly type: BuildingGenerationIssueType.ANNOTATION_MISSING_PARENT;
  readonly handle: AnnotationHandle | ContentTemplateInstanceHandle;
}

export interface HoleOnLevelIssue {
  readonly type: BuildingGenerationIssueType.HOLE_ON_LEVEL;
  readonly handle: LevelHandle;
  readonly holes: PolyLine2[];
}

export interface CeilingGenerationIssue {
  readonly type: BuildingGenerationIssueType.CEILING_GENERATION_ISSUE;
  readonly handle: RoomHandle;
  readonly message: string;
}

export interface MaxBSPDepthExceededIssue {
  readonly type: BuildingGenerationIssueType.MAX_BSP_DEPTH_EXCEEDED;
  readonly handle: DocEntityHandle;
}
